export interface UploadFilePlaceholder {
  name: string
  description?: string
  allowedFileExtensions?: string[]
  maxFileSize?: number
}

export interface UploadFilesActionConfig {
  allowedFileExtensions: string[]
  allowMultiple: boolean
  maxFileSize: number
  maxFiles: number
  placeholders?: UploadFilePlaceholder[] | null
}

export interface NormalizedUploadFilePlaceholder {
  name: string
  description?: string
  allowedFileExtensions: string[]
  maxFileSize: number
}

export interface NormalizedUploadFilesAction extends UploadFilesActionConfig {
  placeholders: NormalizedUploadFilePlaceholder[]
}

export function normalizeUploadFilesAction(action: UploadFilesActionConfig): NormalizedUploadFilesAction {
  const placeholders = (action.placeholders ?? []).map((placeholder) => ({
    ...placeholder,
    name: placeholder.name.trim(),
    allowedFileExtensions: placeholder.allowedFileExtensions ?? [...action.allowedFileExtensions],
    maxFileSize: placeholder.maxFileSize ?? action.maxFileSize,
  }))

  return {
    ...action,
    placeholders,
  }
}

export function parseUploadFileMetadata(metadata: unknown): Record<string, unknown> | null {
  if (typeof metadata !== 'string') {
    return null
  }

  try {
    const parsedMetadata: unknown = JSON.parse(metadata)
    if (parsedMetadata === null || typeof parsedMetadata !== 'object' || Array.isArray(parsedMetadata)) {
      return null
    }

    return parsedMetadata as Record<string, unknown>
  } catch {
    return null
  }
}

export function getUploadFilePlaceholderName(file: { metadata?: unknown | null }): string | null {
  const metadata = parseUploadFileMetadata(file.metadata)
  const placeholder = metadata?.placeholder
  if (typeof placeholder !== 'string') {
    return null
  }

  const normalizedPlaceholder = placeholder.trim()
  return normalizedPlaceholder || null
}

export interface LandingFilePlaceholderGroup<TFile, TPlaceholder extends { name: string }> {
  placeholder: TPlaceholder
  files: TFile[]
}

export interface GroupedLandingFiles<TFile, TPlaceholder extends { name: string }> {
  placeholderFiles: LandingFilePlaceholderGroup<TFile, TPlaceholder>[]
  unassignedFiles: TFile[]
}

export function groupLandingFilesByPlaceholder<
  TFile extends { metadata?: unknown | null },
  TPlaceholder extends { name: string },
>(files: readonly TFile[], placeholders: readonly TPlaceholder[]): GroupedLandingFiles<TFile, TPlaceholder> {
  const placeholderFiles = placeholders.map((placeholder) => ({
    placeholder,
    files: [] as TFile[],
  }))
  const unassignedFiles: TFile[] = []

  for (const file of files) {
    const placeholderName = getUploadFilePlaceholderName(file)
    const group = placeholderFiles.find(({ placeholder }) => placeholder.name.trim() === placeholderName)

    if (group) {
      group.files.push(file)
    } else {
      unassignedFiles.push(file)
    }
  }

  return { placeholderFiles, unassignedFiles }
}

export interface IndexedUploadFile {
  file: File
  placeholder?: string
}

export interface BuildUploadFormDataOptions {
  projectId: number
  appKey: string
  namespace: string
  files: readonly IndexedUploadFile[]
}

export function appendIndexedUploadFiles(formData: FormData, files: readonly IndexedUploadFile[]): void {
  files.forEach(({ file, placeholder }, index) => {
    const fieldIndex = index + 1
    formData.append(`file${fieldIndex}`, file, file.name)

    if (placeholder !== undefined) {
      formData.append(`meta${fieldIndex}`, JSON.stringify({ placeholder }))
    }
  })
}

export function buildIndexedUploadFormData({
  projectId,
  appKey,
  namespace,
  files,
}: BuildUploadFormDataOptions): FormData {
  const formData = new FormData()
  formData.append('projectId', projectId.toString())
  formData.append('appKey', appKey)
  formData.append('namespace', namespace)
  appendIndexedUploadFiles(formData, files)
  return formData
}
